/**
 * Bouton d'installation PWA — capture l'event `beforeinstallprompt` du
 * browser (Chrome Android, Edge desktop, Chrome desktop) et propose un
 * call-to-action visible plutôt que de laisser l'utilisateur chercher
 * "Ajouter à l'écran d'accueil" dans le menu hamburger.
 *
 * iOS Safari n'émet PAS `beforeinstallprompt` — un hint texte s'affiche à
 * la place quand on détecte iOS sans `standalone`, expliquant le geste
 * « Partager → Sur l'écran d'accueil ».
 *
 * Une fois la PWA installée (display:standalone), le composant disparaît
 * et persiste dans localStorage pour ne plus jamais réafficher le hint
 * iOS sur le même appareil (clé `rws.pwa.installed`).
 */
import { createSignal, onCleanup, onMount, Show, type Component } from "solid-js";

// Type minimal de l'event Chrome (non standard).
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const DISMISS_KEY = "rws.pwa.dismissed";
const INSTALLED_KEY = "rws.pwa.installed";

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    // iOS Safari : `navigator.standalone` non standard.
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !(window as { MSStream?: unknown }).MSStream;
}

function dismissedRecently(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || "0");
    if (!at) return false;
    // 7 jours de cooldown avant de re-proposer.
    return Date.now() - at < 7 * 24 * 3_600_000;
  } catch {
    return false;
  }
}

interface PwaInstallButtonProps {
  compact?: boolean;
  label?: string;
  class?: string;
}

function classes(...parts: Array<string | undefined | false>): string {
  return parts.filter(Boolean).join(" ");
}

export const PwaInstallButton: Component<PwaInstallButtonProps> = (props) => {
  const [promptEvent, setPromptEvent] = createSignal<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = createSignal(isStandalone());
  const [showIosHint, setShowIosHint] = createSignal(false);
  const [compactHelpOpen, setCompactHelpOpen] = createSignal(false);

  function onBeforeInstall(e: Event) {
    e.preventDefault();
    setPromptEvent(e as BeforeInstallPromptEvent);
  }
  function onAppInstalled() {
    setInstalled(true);
    setPromptEvent(null);
    try {
      localStorage.setItem(INSTALLED_KEY, "1");
    } catch {
      /* ignore quota */
    }
  }

  onMount(() => {
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onAppInstalled);
    onCleanup(() => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
    });

    // iOS — pas d'event natif, on affiche un hint si toutes les conditions :
    //   - iOS Safari
    //   - pas déjà en mode standalone
    //   - pas déjà dismissé il y a < 7 jours
    //   - pas marqué comme installé dans le localStorage
    if (isIOS() && !isStandalone() && !dismissedRecently()) {
      try {
        if (localStorage.getItem(INSTALLED_KEY) !== "1") setShowIosHint(true);
      } catch {
        setShowIosHint(true);
      }
    }
  });

  async function install() {
    const ev = promptEvent();
    if (!ev) return;
    await ev.prompt();
    const choice = await ev.userChoice;
    if (choice.outcome === "dismissed") {
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
    }
    setPromptEvent(null);
  }

  function dismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setPromptEvent(null);
    setShowIosHint(false);
    setCompactHelpOpen(false);
  }

  return (
    <Show when={!installed()}>
      <Show when={promptEvent()}>
        <button
          type="button"
          class={classes("pwa-install-btn", props.compact && "pwa-install-btn--compact", props.class)}
          onClick={install}
          title="Installer RemoteWS sur cet appareil pour un accès rapide"
        >
          <span aria-hidden="true">⬇</span> {props.label ?? "Installer l'app"}
        </button>
      </Show>
      <Show when={!promptEvent() && showIosHint()}>
        <Show
          when={props.compact}
          fallback={
            <div class="pwa-ios-hint" role="status">
              <span class="pwa-ios-hint__text">
                Installer : <strong>Partager</strong> ⤴ → <strong>Sur l'écran d'accueil</strong>
              </span>
              <button type="button" class="pwa-ios-hint__close" onClick={dismiss} aria-label="Masquer">
                ✕
              </button>
            </div>
          }
        >
          <div class={classes("pwa-install-compact", props.class)}>
            <button
              type="button"
              class="pwa-install-btn pwa-install-btn--compact"
              onClick={() => setCompactHelpOpen((v) => !v)}
              title="Installer RemoteWS sur l'écran d'accueil"
            >
              <span aria-hidden="true">⬇</span> {props.label ?? "Installer"}
            </button>
            <Show when={compactHelpOpen()}>
              <div class="pwa-ios-hint pwa-ios-hint--compact" role="status">
                <span class="pwa-ios-hint__text">
                  <strong>Partager</strong> ⤴ puis <strong>Sur l'écran d'accueil</strong>
                </span>
                <button type="button" class="pwa-ios-hint__close" onClick={dismiss} aria-label="Masquer">
                  ✕
                </button>
              </div>
            </Show>
          </div>
        </Show>
      </Show>
    </Show>
  );
};
